import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
} from 'react';
import { DEFAULT_SETTINGS, RECENT_ROUTINES } from '@/data/presets';
import { audioService } from '@/services/audio-service';

const SETTINGS_KEY = 'tabata:settings';
const ROUTINES_KEY = 'tabata:routines';

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      audio: { ...DEFAULT_SETTINGS.audio, ...(parsed.audio || {}) },
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const loadRoutines = () => {
  try {
    const raw = localStorage.getItem(ROUTINES_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

const initialState = {
  settings: loadSettings(),
  routines: loadRoutines(),
  recents: RECENT_ROUTINES,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'SET_AUDIO':
      return {
        ...state,
        settings: {
          ...state.settings,
          audio: { ...state.settings.audio, ...action.payload },
        },
      };
    case 'SAVE_ROUTINE': {
      const exists = state.routines.some((r) => r.id === action.payload.id);
      const routines = exists
        ? state.routines.map((r) =>
            r.id === action.payload.id ? action.payload : r
          )
        : [action.payload, ...state.routines];
      return { ...state, routines };
    }
    case 'DELETE_ROUTINE':
      return {
        ...state,
        routines: state.routines.filter((r) => r.id !== action.payload),
      };
    default:
      return state;
  }
}

const AppContext = createContext(null);

const applyTheme = (theme) => {
  const root = document.documentElement;
  const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const dark = theme === 'dark' || (theme === 'system' && systemDark);
  root.classList.toggle('dark', dark);
  root.setAttribute('data-theme', dark ? 'dark' : 'light');
};

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
    audioService.setEnabled(state.settings.audio.enabled);
    audioService.setVolume(state.settings.audio.volume);
    applyTheme(state.settings.theme);
  }, [state.settings]);

  useEffect(() => {
    localStorage.setItem(ROUTINES_KEY, JSON.stringify(state.routines));
  }, [state.routines]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(state.settings.theme);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [state.settings.theme]);

  const setSettings = useCallback((payload) => {
    dispatch({ type: 'SET_SETTINGS', payload });
  }, []);
  const setAudio = useCallback((payload) => {
    dispatch({ type: 'SET_AUDIO', payload });
  }, []);
  const saveRoutine = useCallback((routine) => {
    dispatch({ type: 'SAVE_ROUTINE', payload: routine });
  }, []);
  const deleteRoutine = useCallback((id) => {
    dispatch({ type: 'DELETE_ROUTINE', payload: id });
  }, []);

  const value = {
    ...state,
    setSettings,
    setAudio,
    saveRoutine,
    deleteRoutine,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
